import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { createQuoteDealerPricingRequests } from "../../api/quote/createQuoteDealerPricingRequests";
import { getQuote } from "../../api/quote/getQuote";
import { getQuoteDealerPricingRequests } from "../../api/quote/getQuoteDealerPricingRequests";
import APAccordion from "../../components/APAccordion";
import APButton from "../../components/buttons/APButton";
import BackButton from "../../components/buttons/BackButton";
import EmptyList from "../../components/EmptyList";
import APForm from "../../components/form/APForm";
import APTextField from "../../components/form/APTextField";
import PageContainer from "../../components/PageContainer";
import { removeNulls } from "../../helpers/array";
import {
  getCRMClientURL,
  getDefaultVehicleDealerProcurementFee,
} from "../../helpers/configuration";
import { canRequestDealerPricing } from "../../helpers/dossiers";
import {
  getDefaultVehiclePricingRequestSearchParams,
  groupVehiclePricingRequested,
} from "../../helpers/vehiclePricingRequests";
import { useAuth } from "../../providers/AuthProvider";
import { useConfirm } from "../../providers/ConfirmProvider";
import { ErrorCode } from "../../types/Error";
import { VehicleDealer } from "../../types/VehicleDealer";
import PricingRequestList from "./components/PricingRequestList";
import VehicleDealerCard from "./components/VehicleDealerCard";
import VehicleDealerSearch from "./components/VehicleDealerSearch";

export const DEFAULT_PAGE_SIZE = 10;

type RequestDealerPricingInputs = {
  procurementFeeExGst: string;
  exteriorColour: string;
  interiorColour: string;
  noteToDealer: string;
  selectedDealers: VehicleDealer[];
};

const RequestDealerPricing = () => {
  const { quoteId } = useParams();
  const { getScopedPageUrl } = useAuth();
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RequestDealerPricingInputs>({
    mode: "onTouched",
    defaultValues: { selectedDealers: [] },
  });
  const confirm = useConfirm();
  const selectedDealers = watch("selectedDealers");

  if (!quoteId) {
    throw new Error("quote id param is missing");
  }

  const quote = getQuote(quoteId, {
    mutationOptions: {
      errorNavPaths: { [ErrorCode.QuoteDeleted]: getScopedPageUrl("home") },
    },
  });

  const dossierId = quote.data?.identifiers.crmDossierId;
  const dossier = getDossier(dossierId || "", { disabled: !dossierId });

  const dealerPricingRequests = getQuoteDealerPricingRequests(quoteId);
  const config = getPublicConfig();
  const crmClientURL = config.isSuccess ? getCRMClientURL(config.data) : null;

  if (quote.isError) {
    throw quote.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  const {
    mutateAsync: createDealerPricingRequests,
    isLoading: isCreatingDealerPricingRequests,
  } = createQuoteDealerPricingRequests(quoteId, {
    mutationOptions: {
      successAlertMessage:
        "Pricing requests will be sent to the selected dealers",
    },
  });

  const { requested: requestedPricing, received: receivedPricing } = useMemo(
    () => groupVehiclePricingRequested(dealerPricingRequests.data ?? []),
    [dealerPricingRequests.data]
  );

  const onToggleSelectDealer = async (vehicleDealer: VehicleDealer) => {
    const isIncluded = selectedDealers
      .map((d) => d.id)
      .includes(vehicleDealer.id);

    if (isIncluded) {
      const newSelectedDealers = selectedDealers.filter(
        (d) => d.id !== vehicleDealer.id
      );
      setValue("selectedDealers", newSelectedDealers);
    } else {
      const newSelectedDealers = [...selectedDealers, vehicleDealer];
      setValue("selectedDealers", newSelectedDealers);
    }
  };

  const onSubmit = async (data: RequestDealerPricingInputs) => {
    const ok = await confirm({
      title: "Send Pricing Requests",
      description: `This will send Vehicle Pricing Requests to the ${data.selectedDealers.length} selected dealers. Continue?`,
      cancelText: "No",
    });

    if (!ok) {
      return;
    }

    await createDealerPricingRequests({
      procurementFeeExGST: parseFloat(data.procurementFeeExGst),
      requestedExteriorColour: data.exteriorColour,
      requestedInteriorColour: data.interiorColour,
      noteToDealer: data.noteToDealer,
      selectedDealerIds: data.selectedDealers.map((d) => d.id),
    });
    navigate(getScopedPageUrl("viewQuote", quoteId));
  };

  const renderDealers = (dealers: VehicleDealer[]) => {
    const dealerIsSelected = (dealer: VehicleDealer) => {
      return selectedDealers.some((d) => d.id === dealer.id);
    };

    if (dealers.length === 0) {
      return <EmptyList message="No dealers selected" />;
    }

    return dealers.map((dealer) => (
      <VehicleDealerCard
        key={dealer.id}
        vehicleDealer={dealer}
        actions={{
          onToggleSelect: onToggleSelectDealer,
        }}
        config={{
          isSelected: dealerIsSelected(dealer),
          hasSentRequest:
            !!dealerPricingRequests.data &&
            dealerPricingRequests.data.some((d) => d.id === dealer.id),
          crmClientURL: crmClientURL,
        }}
      />
    ));
  };

  useEffect(() => {
    if (quote.data) {
      if (quote.data.preferences.exteriorPaintColourPreference) {
        setValue(
          "exteriorColour",
          quote.data.preferences.exteriorPaintColourPreference
        );
      }
      if (quote.data.preferences.interiorPaintColourPreference) {
        setValue(
          "interiorColour",
          quote.data.preferences.interiorPaintColourPreference
        );
      }
    }
  }, [quote.isSuccess]);

  const isLoading =
    quote.isLoading ||
    dossier.isLoading ||
    config.isLoading ||
    dealerPricingRequests.isLoading ||
    isCreatingDealerPricingRequests;
  const isSuccess =
    quote.isSuccess &&
    dossier.isSuccess &&
    config.isSuccess &&
    dealerPricingRequests.isSuccess;

  // Check if pricing requests are allowed to be sent
  const allowSendPricingRequests =
    dossier.data &&
    quote.data &&
    canRequestDealerPricing(dossier.data, quote.data);

  return (
    <PageContainer
      title="Vehicle Pricing Request"
      loading={isLoading}
      size="full"
    >
      {isSuccess && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="labelLarge">
              {dossier.data.crmContactName} - {quote.data.vehicle.year}{" "}
              {quote.data.vehicle.make} {quote.data.vehicle.model}{" "}
              {quote.data.vehicle.variantName}
            </Typography>
          </Box>
          <Stack
            spacing={0}
            sx={{
              display: "grid",
              gap: 8,
              gridTemplateColumns: { lg: "460px 5fr" },
            }}
          >
            <Box>
              <APForm
                onSubmit={handleSubmit(onSubmit)}
                submitText="Send Pricing Requests"
                isLoading={false}
                isError={Object.keys(errors).length > 0}
                hideSubmitButton={!allowSendPricingRequests}
                disableSubmit={selectedDealers.length === 0}
                mobileButtonPosition="left"
                leftButton={
                  <APButton
                    variant="contained"
                    text={`All ${quote.data.vehicle.make} ${quote.data.vehicle.model} Pricing`}
                    onClick={() =>
                      navigate(
                        `${getScopedPageUrl(
                          "searchDealerPricingRequests"
                        )}?${getDefaultVehiclePricingRequestSearchParams(
                          quote.data
                        ).toString()}`
                      )
                    }
                  />
                }
              >
                <APTextField<RequestDealerPricingInputs>
                  name="procurementFeeExGst"
                  label="Procurement Fee Ex GST"
                  control={control}
                  validations={{
                    formatValidation: /[0-9]+/,
                  }}
                  errors={errors}
                  defaultValue={`${getDefaultVehicleDealerProcurementFee(
                    config.data
                  )}`}
                  startAdornment="$"
                  placeholder="xxxxxxx"
                  formatNumber={true}
                />

                <APTextField<RequestDealerPricingInputs>
                  name="exteriorColour"
                  label="Exterior Colour"
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue=""
                  multiline
                  minRows={1}
                />

                <APTextField<RequestDealerPricingInputs>
                  name="interiorColour"
                  label="Interior Colour"
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue=""
                  multiline
                  minRows={1}
                />

                <APTextField<RequestDealerPricingInputs>
                  name="noteToDealer"
                  label="Note to Dealer"
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue=""
                  multiline
                  minRows={1}
                />

                <APAccordion title="Select Dealers" defaultExpanded>
                  <Stack spacing={2}>{renderDealers(selectedDealers)}</Stack>
                </APAccordion>
              </APForm>
            </Box>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { lg: "1fr 1fr" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {requestedPricing.length > 0 && (
                    <PricingRequestList
                      displayType="accordion"
                      cardType="quote-specific"
                      requests={requestedPricing}
                      type="requested"
                      crmClientURL={crmClientURL}
                      defaultExpanded={false}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  {receivedPricing.length > 0 && (
                    <PricingRequestList
                      displayType="accordion"
                      cardType="quote-specific"
                      requests={receivedPricing}
                      type="received"
                      crmClientURL={crmClientURL}
                      defaultExpanded={false}
                    />
                  )}
                </Box>
              </Box>

              <APAccordion title="Select Dealers" defaultExpanded>
                <VehicleDealerSearch
                  initialFilters={{
                    pageSize: DEFAULT_PAGE_SIZE,
                    make: quote.data.vehicle.make,
                    state: quote.data.vehicle.state,
                    rating: "preferred",
                    nameSearch: null,
                  }}
                  actions={{
                    onToggleSelectDealer: onToggleSelectDealer,
                  }}
                  selectedDealers={selectedDealers}
                  receivedDealerIds={removeNulls(
                    dealerPricingRequests.data?.map((d) => d.vehicleDealerId) ??
                      []
                  )}
                  crmClientURL={crmClientURL}
                />
              </APAccordion>
            </Stack>
          </Stack>
          <Box>
            <BackButton />
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default RequestDealerPricing;
